// @ts-nocheck
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { ChartingLibrary } from 'components/ChartingLibrary';
import { useMemo, useRef, useEffect } from 'react';
import { useTheme, Theme } from '@mui/material/styles';
import { ColorMode } from 'theme';
import {
  IChartingLibraryWidget,
  ChartingLibraryWidgetOptions,
  IBasicDataFeed,
  OnReadyCallback,
  LibrarySymbolInfo,
  ResolutionString,
  PeriodParams,
  HistoryCallback,
  DatafeedErrorCallback,
  SearchSymbolsCallback,
  ResolveCallback,
  SearchSymbolResultItem,
  SubscribeBarsCallback,
  VisibleTimeRange,
  IDropdownApi,
  ISymbolValueFormatter,
  CustomIndicator,
  StudyInputType,
  PineJS,
  StudyTargetPriceScale,
  IStudyApi,
  EntityInfo,
  StudyInputValueItem,
  SymbolResolveExtension,
  LineStyle,
} from 'charting_library';
import { DefaultService } from 'gen/stream-api/requests/services.gen';
import {
  ComboSet,
  Sym,
  OptionAccumulationType,
  OptionOrigin,
  CandleTOHLCV,
} from 'gen/stream-api/requests/types.gen';
import {
  FetchAuxOptions,
  CandleTupleTOHLCV,
  CandleTupleSTOHLC,
  candleSTOHLCfromTuple,
  PriceLineKeys,
  OptionAccumulationTypes,
  SymbolLineKind,
  SymbolLineParams,
  nameFromSymbolLineParams,
  decodeSymbolLineParams,
  HiroIndicatorOptionOrigins,
  RawHiroAndPrice,
  HiroAndPrice,
  HiroAlertCategoryMap,
  AlertData,
  OPTION_ORIGIN_TO_PALETTE,
  OPTION_ACCUM_TO_PALETTE,
  HiroOverview,
} from 'types';
import {
  hiroChartTimeRangeState,
  timezoneState,
  stocksState_SUBSCRIBE_TO_POLLING_RENDER,
  currentSymHiroOverviewState,
  workerState,
  hiroAlertsState,
} from 'states';
import { unsupressedSetInterval } from 'util/poll';
import { hiroSymbolsState } from 'states/hiro';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { getEquitiesBySyms, hiroV2HistoryLimit } from 'util/hiro';
import { formatAsCompactNumber } from 'util/shared/common';
import { dayjs, ET, getClosestBusinessDay, sleep } from 'util/shared';
import { useLog } from 'hooks';
import { useHiroCtx } from './HiroProvider';
import {
  useStreamingSocketCore,
  SendStreamControlMsgFn,
} from 'hooks/bloomberg/streaming/useStreamingSocket';
import { StreamType } from 'types/shared';
import { REVERSE_FIELD_NAME_MAPPING } from 'config/equityhub';

import { Dayjs } from 'dayjs';
import sortedIndexBy from 'lodash/sortedIndexBy';
import zip from 'lodash/zip';
import debounce from 'lodash/debounce';

import { convertData } from './HiroChart/convertData';
import { AlertsManager, REFETCH_ALERTS_INTERVAL_MS } from './AlertsManager';
import { KeyLevelsManager } from './KeyLevelsManager';

const HISTORY_BEGIN = dayjs('2025-04-08T00:00:00Z');
const HISTORY_NEXT_EXPIRY_BEGIN = dayjs('2025-07-01T00:00:00Z');

const SECONDS_IN_DAY = 24 * 60 * 60;
export const MINUTES_IN_DAY = 24 * 60;
const REPLACE_STREAM_DATA_INTERVAL_MS = 5 * 60 * 1_000;

const DEFAULT_CANDLE_DURATION = 60 * 1_000;
const GET_BARS_RECURSION_LIMIT = 6;
const HIRO_BASE_CANDLE_DURATION_MS = 5 * 1_000;
const SCYLLA_CANDLES_RESYNC_INTERVAL_MS = 10 * 1_000;

// leading space is significant -- used to put Spotgamma indicators at the
// top of indicators search
const HIRO_SIGNALS_INDICATOR_DESCRIPTION = ' Spotgamma HIRO Signals';
const HIRO_KEY_LEVELS_INDICATOR_DESCRIPTION = ' Spotgamma Key Levels';
const HIRO_ALERT_INDICATOR_DESCRIPTION = ' Spotgamma Alerts';

const ONE_DAY_IN_ONE_MIN_INTERVAL_DESC = '1 day in 1 minute intervals';

const ohlc_labels = ['open', 'high', 'low', 'close'];

const SUPPORTED_RESOLUTIONS = [
  '5s',
  '10s',
  '30s',
  '1', // minute
  '2', // minutes
  '5', // minutes
  '10', // minutes
  '15', // minutes
  '30', // minutes
] as ResolutionString[];

type CandleTOHLCVS = CandleTOHLCV & {
  // A monotonically increasing number that is used to indicate the recency of a
  // received candle is. This enables the updating of the in-memory candle array with
  // new candles as they come in and as they become more correct/more canonical.
  // This is due to the out-of-order nature of candles being parsed from dxfeed
  curGetBarsRequestCnt?: number;
};

const availableAlerts = ['HIRO Flow Negative', 'HIRO Flow Positive'].map(
  (alertCategory) => ({
    id: alertCategory.replace(' ', '_'),
    alertCategory,
  }),
);

interface ChunkWindow {
  beginUnixTsInclusive: number;
  endUnixTsExclusive: number;
}

export class SignalSeries {
  name: string;
  symbolLineParams: SymbolLineParams;

  // a contiguous array of candles from the server
  candles: CandleTOHLCVS[] = [];

  // the range requested from the server, which may return
  // results that don't start and end _exactly_ at the
  // requested boundaries
  requestedRangeBeg?: number;
  requestedRangeEnd?: number;

  latestFetches: Promise<void> = Promise.resolve();

  // set through interface `subscribeBars`
  subscriberUID?: string;
  onRealtimeCallback?: SubscribeBarsCallback;
  hiroCandlesStreamPollInterval?: ReturnType<typeof setInterval>;

  // stores the rolling accumulation number of the current window for this
  // series
  windowAccumulator?: number;

  // stores the current window's begin time
  windowBeginUnixTs?: number;
  windowAccumulatorTs?: number;

  constructor(name: string) {
    this.name = name;
    this.symbolLineParams = decodeSymbolLineParams(name);
  }
}

export interface FetchRange {
  rangeBeg: dayjs.Dayjs;
  rangeEnd: dayjs.Dayjs;
}

interface GetFetchRangesResult {
  fetchRanges: FetchRange[];
  beg: dayjs.Dayjs;
  end: dayjs.Dayjs;
}

export class HiroChartDatafeed implements IBasicDataFeed {
  fetchAPIWithLog?: (
    endpoint: string,
    auxiliaryOptions?: FetchAuxOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;

  candleDurationMs: number = DEFAULT_CANDLE_DURATION;

  availableUnderlyings?: Sym[];
  availableCombos?: ComboSet[];
  availableSymbols?: SearchSymbolResultItem[];
  windowBegin?: number;
  beginVisibleRange?: dayjs.Dayjs;
  endVisibleRange?: dayjs.Dayjs;
  chartingLibraryWidget?: IChartingLibraryWidget;
  rollingWindowMinutes: number = MINUTES_IN_DAY;

  signalSeries: Record<string, SignalSeries> = {};
  sendStreamControlMsg?: SendStreamControlMsgFn;

  latestKeyLevelFetch: Promise<void> = Promise.resolve();
  latestAlertFetch: Promise<void> = Promise.resolve();
  currentSession?: string;
  lastDataReset: dayjs.Dayjs = dayjs();
  broadCastTimeRangeUpdate?: (update: HiroTimeRange) => void;

  currentGlobalStocksState?: Map<string, HiroOverview>;
  setCurrentSymHiroOverview?: (v: HiroOverview) => void;

  // so... in order to make TV charting_libary not flicker, you need to synchronize
  // onHistoryCallback calls..
  // https://github.com/tradingview/charting_library/issues/5570
  onHistoryCallbackQueue: [HistoryCallback, Parameters<HistoryCallback>][] = [];
  onHistoryCallbackQueueDebouncer?: () => void;

  keyLevelsManager: KeyLevelsManager;
  alertsManager: AlertsManager;

  theme?: Theme;

  getBarsRequestCnt: number = 0;

  settingsModifierObserver?: MutationObserver;
  debouncedSaveChart: () => void;

  enableSavingChart: boolean;

  constructor(
    rollingWindowMinutes?: number,
    setHiroAlerts: (v: AlertCategory[]) => void,
  ) {
    this.keyLevelsManager = new KeyLevelsManager(this);
    this.alertsManager = new AlertsManager(this, setHiroAlerts);
    if (rollingWindowMinutes) {
      this.rollingWindowMinutes = rollingWindowMinutes;
    }
    this.debouncedSaveChart = debounce(this.saveChart.bind(this), 2_000);
    this.enableSavingChart = false;
    // initial loading of chart causes save debouncer to be called.  Suppressing
    // saves initially - TODO - should root cause the event sequences
    setTimeout(() => {
      this.enableSavingChart = true;
    }, 10_000);
  }

  destructor() {
    this.settingsModifierObserver?.disconnect();
  }

  onReady(callback: OnReadyCallback): void {
    setTimeout(() => {
      callback({
        supported_resolutions: SUPPORTED_RESOLUTIONS,
        supports_marks: false,
        supports_timescale_marks: false,
      });
    }, 0);
  }

  resolveSymbol(
    symbolName: string,
    onSymbolResolvedCallback: ResolveCallback,
    _onResolveErrorCallback: DatafeedErrorCallback,
    extension?: SymbolResolveExtension,
  ): void {
    // TV charting_library typescript interface lies about existence of `session` on `SymbolResolveExtension` type
    if (this.currentSession !== (extension?.session ?? 'regular')) {
      this.currentSession = extension.session ?? 'regular';
      this.resetData();
    }

    const symbolLineParams = decodeSymbolLineParams(symbolName);
    const subsessions = [
      {
        description: 'Regular Trading Hours',
        id: 'regular',
        session: '0930-1600',
      },
      {
        description: 'Extended Trading Hours',
        id: 'extended',
        session: '0000-0000',
      },
    ];
    const currentSession = subsessions.find(
      (s) => (extension?.session ?? 'regular') === s.id,
    );

    // required by charting library so that it doesn't happen on the same event queue tick
    setTimeout(
      () =>
        onSymbolResolvedCallback({
          name: symbolName,
          ticker: symbolName,
          type: 'stock',
          timezone: 'America/New_York',
          minmov: 1,
          pricescale: 1,
          has_intraday: true,
          has_seconds: true,
          supported_resolutions: SUPPORTED_RESOLUTIONS,
          description: symbolName,
          exchange: 'NYSE',
          listed_exchange: 'NYSE',
          format: symbolLineParams.kind === 'price' ? 'price' : 'undefined',
          session: currentSession.session,
          subsession_id: currentSession.id,
          subsessions,
        }),
      0,
    );
  }

  /// Get the beginning of the rolling window that contains the given timestamp
  windowFromUnixTs(
    unixTimestamp: number,
    rollingWindowMinutes?: number,
  ): ChunkWindow {
    const rollingMinutes = rollingWindowMinutes ?? this.rollingWindowMinutes;
    let beginUnixTsInclusive = 0;
    if (!isFinite(rollingMinutes) || rollingMinutes >= MINUTES_IN_DAY) {
      let begin = dayjs
        .unix(
          rollingMinutes > MINUTES_IN_DAY && unixTimestamp > this.windowBegin
            ? this.windowBegin
            : unixTimestamp,
        )
        .tz(ET);
      if (this.currentSession === 'extended') {
        begin = begin.startOf('date');
      } else {
        if (begin < begin.hour(9).minute(30).second(0).millisecond(0)) {
          begin = begin.subtract(1, 'day');
        }
        begin = begin.hour(9).minute(30).second(0).millisecond(0);
      }

      beginUnixTsInclusive = begin.unix();
    } else {
      beginUnixTsInclusive =
        Math.floor(unixTimestamp / (rollingMinutes * 60)) * rollingMinutes * 60;
    }
    const endUnixTsExclusive = beginUnixTsInclusive + rollingMinutes * 60;
    return {
      beginUnixTsInclusive,
      endUnixTsExclusive,
    };
  }

  getOrCreateSeries(seriesName: string): SignalSeries {
    return (this.signalSeries[seriesName] =
      this.signalSeries[seriesName] ?? new SignalSeries(seriesName));
  }

  onHistoryCallbackQueueSender() {
    const onHistoryCallbackQueue = this.onHistoryCallbackQueue;
    this.onHistoryCallbackQueue = [];
    for (const [historyCallback, params] of onHistoryCallbackQueue) {
      historyCallback(...params);
    }
  }

  enqueueOnHistoryCallback(
    clbk: HistoryCallback,
    params: Parameters<HistoryCallback>,
  ) {
    if (!this.onHistoryCallbackQueueDebouncer) {
      this.onHistoryCallbackQueueDebouncer = debounce(
        this.onHistoryCallbackQueueSender.bind(this),
        300,
      );
    }
    this.onHistoryCallbackQueue.push([clbk, params]);
    this.onHistoryCallbackQueueDebouncer();
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onHistoryCallback: HistoryCallback,
    onErrorCallback: DatafeedErrorCallback,
    recursiveCnt?: number,
  ): Promise<void> {
    this.getBarsRequestCnt += 1;
    const curGetBarsRequestCnt = this.getBarsRequestCnt;
    if (recursiveCnt >= GET_BARS_RECURSION_LIMIT) {
      this.enqueueOnHistoryCallback(onHistoryCallback, [[]]);
      return;
    }

    const historyLimit = hiroV2HistoryLimit();
    if (dayjs.unix(periodParams.to) < historyLimit) {
      this.enqueueOnHistoryCallback(onHistoryCallback, [[], { noData: true }]);
      return;
    }
    if (periodParams.from < historyLimit.unix()) {
      periodParams.from = historyLimit.unix();
    }

    const now = dayjs().tz(ET);
    const symbolLineParams = decodeSymbolLineParams(symbolInfo.name);

    if (!symbolLineParams?.symbol.length) {
      onErrorCallback(`Invalid symbol format - '${symbolInfo.name}'`);
      return;
    }

    const series = this.getOrCreateSeries(symbolInfo.name);

    await series.latestFetches;

    const { fetchRanges, beg, end } = this.getFetchRanges(
      series,
      periodParams,
      now,
    );

    // eslint-disable-next-line @typescript-eslint/await-thenable
    const candlesReqs = await this.getCandleReqsFromFetchRanges(
      fetchRanges,
      end,
      now,
      symbolLineParams,
    );

    series.latestFetches = Promise.all(candlesReqs)
      .then((responses) => {
        responses.forEach((rsp: { candles: CandleTupleTOHLCV[] }) => {
          if (!rsp.candles.length) {
            return;
          }

          const candles: CandleTOHLCVS[] = rsp.candles.map(
            ([time, open, high, low, close, volume]: CandleTupleTOHLCV) => ({
              time,
              open,
              high,
              low,
              close,
              volume,
              curGetBarsRequestCnt,
            }),
          );
          if (candles[0].time < series.candles[0]?.time) {
            [].push.apply(candles, series.candles);
            series.candles = candles;
          } else {
            [].push.apply(series.candles, candles);
          }
          series.candles.sort(
            (a, b) =>
              a.time - b.time ||
              // if times match, order most recently fetched candles first for
              // duplicate removal in the next step
              (b?.curGetBarsRequestCnt ?? 0) - (a?.curGetBarsRequestCnt ?? 0),
          );

          // remove duplicates, if any.  Possibly caused by concurrent requests
          series.candles = series.candles.filter(
            (candle, index, self) =>
              index == 0 || candle.time !== self[index - 1].time,
          );
          series.requestedRangeBeg = beg.valueOf();
          series.requestedRangeEnd = end.valueOf();
        });

        const { beginUnixTsInclusive: periodFirstWindowBeg } =
          this.windowFromUnixTs(periodParams.from);

        const periodEnd =
          this.windowFromUnixTs(periodParams.to).beginUnixTsInclusive +
          this.rollingWindowMinutes * 60;

        let periodFirstWindowIdx =
          sortedIndexBy(
            series.candles,
            { time: periodFirstWindowBeg } as CandleTOHLCVS,
            'time',
          ) ?? 0;
        periodFirstWindowIdx = Math.max(periodFirstWindowIdx, 0);

        const periodEndIdx =
          sortedIndexBy(
            series.candles,
            { time: periodEnd * 1_000 } as CandleTOHLCVS,
            'time',
          ) ?? 0;

        let lastClose = 0;
        let items = [];
        if (
          symbolLineParams.kind === 'price' ||
          symbolLineParams.kind === 'key level' ||
          symbolLineParams.kind === 'alert'
        ) {
          items = series.candles.slice(
            periodFirstWindowIdx,
            Math.min(series.candles.length, periodEndIdx),
          );
          // TODO(ross) 2025.08.18 - debugging
          // if (symbolLineParams.kind === 'alert' && items.length === 0) {
          //   items = [
          //     {
          //       time:
          //         ((periodParams.to - periodParams.from) / 2 +
          //           periodParams.from) *
          //         1_000,
          //       open: 6_440,
          //       high: 6_440,
          //       low: 6_440,
          //       close: 6_440,
          //       volume: 6_440,
          //     },
          //   ];
          // }
        } else if (
          symbolLineParams.kind === 'hiro' ||
          symbolLineParams.kind === 'oldhiro'
        ) {
          let curWindow: ChunkWindow | undefined = undefined;
          for (
            let i = periodFirstWindowIdx;
            i <= periodEndIdx && i < series.candles.length;
            i++
          ) {
            const c = series.candles[i];

            const tsUnix = c.time / 1_000;
            if (
              !curWindow ||
              !(
                curWindow.beginUnixTsInclusive <= tsUnix &&
                curWindow.endUnixTsExclusive > tsUnix
              )
            ) {
              lastClose = 0;
              curWindow = this.windowFromUnixTs(tsUnix);
            }

            const result = {
              time: c.time,
              open: lastClose + c.open,
              high: lastClose + c.high,
              low: lastClose + c.low,
              close: lastClose + c.close,
              volume: c.volume,
            };
            lastClose = result.close;
            series.windowAccumulator = lastClose;
            series.windowBeginUnixTs = curWindow.beginUnixTsInclusive;
            series.windowAccumulatorTs = result.time;

            items.push(result);
          }
        }

        const resultItems = items.filter(
          (item) =>
            item.time / 1_000 >= periodFirstWindowBeg &&
            item.time / 1_000 <= periodParams.to,
        );

        if (resultItems.length === 0) {
          const { beginUnixTsInclusive: nextLookbackBeg } =
            this.windowFromUnixTs(
              periodParams.from - SECONDS_IN_DAY,
              MINUTES_IN_DAY,
            );
          if (
            nextLookbackBeg > HISTORY_BEGIN.unix() &&
            ((symbolLineParams.kind === 'hiro' &&
              symbolLineParams.deltaLineParams.optionOrigin !== 'NextExpiry') ||
              nextLookbackBeg > HISTORY_NEXT_EXPIRY_BEGIN.unix())
          ) {
            void this.getBars(
              symbolInfo,
              resolution,
              { ...periodParams, from: nextLookbackBeg },
              onHistoryCallback,
              onErrorCallback,
              (recursiveCnt ?? 0) + 1,
            );
            return;
          }
        }

        // TODO - remove when chart more solid
        console.log(
          symbolInfo.name,
          dayjs.unix(periodParams.from).format(),
          dayjs.unix(periodParams.to).format(),
          resultItems.length,
          'candles',
        );
        this.enqueueOnHistoryCallback(onHistoryCallback, [resultItems]);
      })
      .catch((error: string) => {
        onErrorCallback(error);
      });
  }

  streamTypeFromSymbolLineParams(
    symbolLineParams: SymbolLineParams,
  ): StreamType {
    if (symbolLineParams.kind === 'price') {
      return StreamType.PRICES;
    } else if (symbolLineParams.kind === 'hiro') {
      const p = symbolLineParams.deltaLineParams;
      switch (p.optionAccumulationType) {
        case 'Total':
          switch (p.optionOrigin) {
            case 'All':
              return p.isFiltered
                ? StreamType.FilteredTotalDelta
                : StreamType.AbsoluteTotalDelta;
            case 'NextExpiry':
              return p.isFiltered
                ? StreamType.FilteredNextExpTotalDelta
                : StreamType.AbsoluteNextExpTotalDelta;
            default:
              throw new Error(`Unknown optionOrigin: ${p.optionOrigin}`);
          }

        case 'Calls':
          switch (p.optionOrigin) {
            case 'All':
              if (p.isFiltered) {
                return StreamType.FilteredCallDelta;
              } else {
                throw new Error(`Unfiltered Call Delta is not supported`);
              }
            case 'NextExpiry':
              if (p.isFiltered) {
                return StreamType.FilteredNextExpCallDelta;
              } else {
                throw new Error(
                  `Unfiltered Next Expiry Call Delta is not supported`,
                );
              }
            default:
              throw new Error(`Unknown optionOrigin: ${p.optionOrigin}`);
          }

        case 'Puts':
          switch (p.optionOrigin) {
            case 'All':
              if (p.isFiltered) {
                return StreamType.FilteredPutDelta;
              } else {
                throw new Error(`Unfiltered Put Delta is not supported`);
              }
            case 'NextExpiry':
              if (p.isFiltered) {
                return StreamType.FilteredNextExpPutDelta;
              } else {
                throw new Error(
                  `Unfiltered Next Expiry Put Delta is not supported`,
                );
              }
            default:
              throw new Error(`Unknown optionOrigin: ${p.optionOrigin}`);
          }

        default:
          throw new Error(
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `Unknown optionAccumulationType: ${p.optionAccumulationType}`,
          );
      }
    } else {
      throw new Error(
        `Cannot ask for stream type for symbolLineParams.kind=${symbolLineParams.kind}`,
      );
    }
  }

  symbolLineParamsFromStreamType(
    symbol: string,
    streamType: StreamType,
  ): SymbolLineParams {
    const result = {
      symbol,
    } as SymbolLineParams;

    if (streamType === StreamType.PRICES) {
      result.kind = 'price';
    } else {
      result.kind = 'hiro';
      let deltaLineTuple:
        | [OptionAccumulationType, OptionOrigin, boolean]
        | undefined;
      switch (streamType) {
        case StreamType.FilteredTotalDelta:
          deltaLineTuple = ['Total', 'All', true];
          break;
        case StreamType.AbsoluteTotalDelta:
          deltaLineTuple = ['Total', 'All', false];
          break;
        case StreamType.FilteredNextExpTotalDelta:
          deltaLineTuple = ['Total', 'NextExpiry', true];
          break;
        case StreamType.AbsoluteNextExpTotalDelta:
          deltaLineTuple = ['Total', 'NextExpiry', false];
          break;

        case StreamType.FilteredCallDelta:
          deltaLineTuple = ['Calls', 'All', true];
          break;
        case StreamType.FilteredNextExpCallDelta:
          deltaLineTuple = ['Calls', 'NextExpiry', true];
          break;

        case StreamType.FilteredPutDelta:
          deltaLineTuple = ['Puts', 'All', true];
          break;
        case StreamType.FilteredNextExpPutDelta:
          deltaLineTuple = ['Puts', 'NextExpiry', true];
          break;

        default:
          throw new Error(`Unknown StreamType: ${streamType}`);
      }
      result.deltaLineParams = {
        optionAccumulationType: deltaLineTuple[0],
        optionOrigin: deltaLineTuple[1],
        isFiltered: deltaLineTuple[2],
      };
    }

    return result;
  }

  subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onRealtimeCallback: SubscribeBarsCallback,
    subscriberUID: string,
    _onResetCacheNeededCallback: () => void,
  ): void {
    const series = this.getOrCreateSeries(symbolInfo.name);
    if (
      series.symbolLineParams.kind === 'key level' ||
      series.symbolLineParams.kind == 'alert' ||
      series.symbolLineParams.kind == 'oldhiro'
    ) {
      return;
    }
    series.onRealtimeCallback = onRealtimeCallback;
    series.subscriberUID = subscriberUID;
    if (
      series.symbolLineParams.kind == 'price' ||
      series.symbolLineParams.kind == 'hiro'
    ) {
      this.subscribeSeriesStreamCandles(series);
    } else if (series.symbolLineParams.kind == 'oldhiro') {
      series.hiroCandlesStreamPollInterval = setInterval(() => {
        const lastCandle = series.candles[series.candles.length - 1];
        if (!lastCandle) {
          return;
        }
        const from = Math.floor(lastCandle.time / (60 * 1_000)) * 60;
        const to =
          (Math.floor(dayjs().valueOf() / HIRO_BASE_CANDLE_DURATION_MS) *
            HIRO_BASE_CANDLE_DURATION_MS) /
          1_000;
        void this.getBars(
          symbolInfo,
          resolution,
          {
            from,
            to,
            countback: 0,
          },
          (candles) => {
            for (const c of candles) {
              if (c.time >= lastCandle.time) {
                onRealtimeCallback(c);
              }
            }
          },
          () => {},
        );
      }, SCYLLA_CANDLES_RESYNC_INTERVAL_MS);
    }
  }

  subscribeSeriesStreamCandles(series: SignalSeries): void {
    this.sendStreamControlMsg!({
      action: 'subscribe',
      underlyings: [series.symbolLineParams.symbol],
      stream_types: this.streamTypeFromSymbolLineParams(
        series.symbolLineParams,
      ),
    });
  }

  unsubscribeBars(subscriberUID: string): void {
    const series = Object.values(this.signalSeries).find(
      (series) => series.subscriberUID === subscriberUID,
    );
    if (series) {
      clearInterval(series.hiroCandlesStreamPollInterval);
      series.hiroCandlesStreamPollInterval = undefined;
      if (series.symbolLineParams.kind !== 'price') {
        return;
      }
      this.sendStreamControlMsg!({
        action: 'unsubscribe',
        underlyings: [series.symbolLineParams.symbol],
        stream_types: this.streamTypeFromSymbolLineParams(
          series.symbolLineParams,
        ),
      });
    }
  }

  async listenCandlesStream(
    streamCandle: CandleTupleSTOHLC,
    streamType: StreamType,
  ) {
    const candleSTOHLC = candleSTOHLCfromTuple(streamCandle);
    const { symbol, open, high, low, close } = candleSTOHLC;
    let time = Number(candleSTOHLC.time);
    const lineParams = this.symbolLineParamsFromStreamType(symbol, streamType);

    if (lineParams.kind === 'hiro') {
      time = Math.floor(time / this.candleDurationMs) * this.candleDurationMs;
    }
    let candle = {
      time,
      open,
      high,
      low,
      close,
      curGetBarsRequestCnt: this.getBarsRequestCnt,
    };

    const seriesName = nameFromSymbolLineParams(lineParams);
    const series = this.signalSeries[seriesName];
    if (!series) {
      // ignoring candle that we don't care about
      return;
    }

    await series.latestFetches;

    if (lineParams.kind === 'price') {
      series.candles.push(candle);
    } else if (lineParams.kind === 'hiro') {
      // delta candles
      const lastCandle = series.candles[series.candles.length - 1];
      if (lastCandle == null || series.windowAccumulator == null) {
        return;
      }

      const { beginUnixTsInclusive: thisWindowBegin } = this.windowFromUnixTs(
        candle.time / 1_000,
      );

      if (lastCandle.time !== series.windowAccumulatorTs) {
        // recalculate window accumulator
        const { beginUnixTsInclusive: lastWindowBegin } = this.windowFromUnixTs(
          lastCandle.time / 1_000,
        );
        let periodFirstWindowIdx =
          sortedIndexBy(
            series.candles,
            { time: lastWindowBegin } as CandleTOHLCVS,
            'time',
          ) ?? 0;
        periodFirstWindowIdx = Math.max(periodFirstWindowIdx, 0);
        let windowAccumulator = 0;

        let curWindow: ChunkWindow | undefined = undefined;
        for (let i = periodFirstWindowIdx; i < series.candles.length; i++) {
          const c = series.candles[i];
          const tsUnix = c.time / 1_000;
          if (
            !curWindow ||
            !(
              curWindow.beginUnixTsInclusive <= tsUnix &&
              curWindow.endUnixTsExclusive > tsUnix
            )
          ) {
            windowAccumulator = 0;
            curWindow = this.windowFromUnixTs(tsUnix);
          }
          windowAccumulator += c.close;
        }
        series.windowAccumulator = windowAccumulator;
        series.windowAccumulatorTs = lastCandle.time;
        series.windowBeginUnixTs = curWindow.beginUnixTsInclusive;
      }

      if (lastCandle.time >= candle.time) {
        // unwind window accumulator to be used to re-calculate the windows value
        series.windowAccumulator = series.windowAccumulator - lastCandle.close;

        lastCandle.high = Math.max(
          lastCandle.high,
          lastCandle.close + candle.high,
        );
        lastCandle.low = Math.min(
          lastCandle.low,
          lastCandle.close + candle.low,
        );
        lastCandle.close += candle.close;
        candle = lastCandle;
      } else {
        if (series.windowBeginUnixTs != thisWindowBegin) {
          series.windowAccumulator = 0;
          series.windowAccumulatorTs = candle.time;
          series.windowBeginUnixTs = thisWindowBegin;
        }
        series.candles.push(candle);
      }
      // now going to be mutated and accumulated and passed to charting_library
      candle = { ...candle };

      candle.open += series.windowAccumulator;
      candle.high += series.windowAccumulator;
      candle.low += series.windowAccumulator;
      candle.close += series.windowAccumulator;
      series.windowAccumulator = candle.close;
    }

    if (series.onRealtimeCallback) {
      // update the global ticker state, if applicable
      const hiroOverview = this.currentGlobalStocksState?.get(
        lineParams.symbol,
      );
      if (
        hiroOverview &&
        lineParams.kind === 'hiro' &&
        lineParams.deltaLineParams.optionAccumulationType === 'Total' &&
        lineParams.deltaLineParams.optionOrigin === 'All' &&
        lineParams.deltaLineParams.isFiltered &&
        this.rollingWindowMinutes === 1_440
      ) {
        hiroOverview.signal = candle.close;
        this.setCurrentSymHiroOverview?.({ ...hiroOverview });
      }

      if (hiroOverview && lineParams.kind === 'price') {
        hiroOverview.price = candle.close;
        this.setCurrentSymHiroOverview?.({ ...hiroOverview });
      }

      series.onRealtimeCallback(candle);
    }
  }

  reRegisterCandleSubscriptions(
    _sendStreamControlMsg: SendStreamControlMsgFn,
  ): void {
    for (const series of Object.values(this.signalSeries)) {
      switch (series.symbolLineParams.kind) {
        case 'key level':
        case 'alert':
        case 'oldhiro':
        case 'hiro':
          continue;
      }
      this.subscribeSeriesStreamCandles(series);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async searchSymbols(
    userInput: string,
    _exchange: string,
    _symbolType: string,
    onResult: SearchSymbolsCallback,
  ): Promise<void> {
    if (!this.availableSymbols) {
      const { underlyings, combos } = await DefaultService.getSgManifest();

      let availableSymbols = underlyings.map((sym) => ({
        symbol: sym,
        full_name: sym,
        description: sym,
        exchange: 'NYSE',
        type: 'undefined',
      }));

      availableSymbols = availableSymbols.concat(
        combos.map((combo) => ({
          symbol: combo.combo,
          full_name: combo.combo,
          description: `Combined signal of ${combo.symbols.join(', ')}`,
          exchange: 'NYSE',
          type: 'undefined',
        })),
      );
      this.availableSymbols = [
        ...availableSymbols.flatMap((sym) =>
          OptionAccumulationTypes.flatMap((optionAccumulationType) =>
            HiroIndicatorOptionOrigins.flatMap((optionOrigin) =>
              [true]
                .map((isFiltered) => {
                  // unsupported combination
                  if (optionAccumulationType != 'Total' && !isFiltered) {
                    return undefined;
                  }
                  const symbol = nameFromSymbolLineParams({
                    symbol: sym.symbol,
                    kind: 'hiro',
                    deltaLineParams: {
                      optionAccumulationType,
                      optionOrigin,
                      isFiltered,
                    },
                  });
                  return {
                    symbol,
                    full_name: symbol,
                    description: `HIRO for ${sym.description}, limited to ${optionAccumulationType}, for ${optionOrigin} trades.`,
                    exchange: sym.exchange,
                    type: 'undefined',
                    optionAccumulationType,
                    optionOrigin,
                    isFiltered,
                  };
                })
                .filter(Boolean),
            ),
          ),
        ),
        ...availableSymbols.map((sym) => {
          const symbol = nameFromSymbolLineParams({
            symbol: sym.symbol,
            kind: 'price',
          });
          return {
            symbol,
            full_name: symbol,
            description: `Price for ${sym.description}`,
            exchange: sym.exchange,
            type: 'stock',
            isPrice: true,
          };
        }),
      ];
    }
    const userInputTokens = userInput
      .split(' ')
      .filter((s) => s.length > 0)
      .map((t) => t.toLowerCase());

    const results = this.availableSymbols
      .filter((sym) => {
        const symLower = sym.symbol.toLowerCase();
        return userInputTokens.reduce(
          (accum, t) =>
            accum &&
            (symLower.includes(t) || sym.description.toLowerCase().includes(t)),
          true,
        );
      })
      .sort(hiroSearchSortFnGen(userInputTokens))
      .slice(0, 100);

    onResult(results);
  }

  getFetchRanges(
    series: SignalSeries,
    periodParams: PeriodParams,
    now: dayjs.Dayjs,
  ): GetFetchRangesResult {
    let windowBegin = this.windowBegin;

    if (!windowBegin || !this.endVisibleRange) {
      const visibleRange = this.chartingLibraryWidget
        ?.activeChart()
        .getVisibleRange();
      if (!windowBegin) {
        windowBegin = this.windowFromUnixTs(
          visibleRange?.from ?? periodParams.from,
        ).beginUnixTsInclusive;
      }
      if (!this.endVisibleRange) {
        this.endVisibleRange = dayjs.unix(visibleRange?.to ?? periodParams.to);
      }
    }

    let maxFetchTime = this.endVisibleRange.add(1, 'day');
    if (maxFetchTime > now) {
      maxFetchTime = now;
    }

    let beg = dayjs
      .unix(
        this.windowFromUnixTs(periodParams.from, MINUTES_IN_DAY)
          .beginUnixTsInclusive,
      )
      .tz(ET);

    if (beg < HISTORY_BEGIN) {
      beg = HISTORY_BEGIN;
    }

    let end = dayjs.unix(periodParams.to);
    const window = this.windowFromUnixTs(now.unix(), MINUTES_IN_DAY);
    if (end.unix() < window.beginUnixTsInclusive) {
      end = dayjs.unix(window.endUnixTsExclusive);
    }
    if (end > maxFetchTime) {
      end = maxFetchTime;
    }
    end = end.tz(ET);

    // TODO - remove when chart more solid
    // console.log(
    //   'RANGE ' + series.name,
    //   '\n   periodParams.from=',
    //   dayjs.unix(periodParams.from).format(),
    //   '\n     periodParams.to=',
    //   dayjs.unix(periodParams.to).format(),
    //   '\n                 beg=',
    //   beg.format(),
    //   '\n                 end=',
    //   end.format(),
    // );

    // check for non-overlapping/abutting ranges
    if (
      (series.requestedRangeBeg &&
        end.valueOf() < series.requestedRangeBeg - this.candleDurationMs) ||
      (series.requestedRangeEnd &&
        beg.valueOf() > series.requestedRangeEnd + this.candleDurationMs)
    ) {
      series.candles = [];
      series.requestedRangeBeg = undefined;
      series.requestedRangeEnd = undefined;
    }

    const fetchRanges = [];
    if (series.candles.length == 0) {
      fetchRanges.push({ rangeBeg: beg, rangeEnd: end });
    } else {
      if (beg.valueOf() < series.requestedRangeBeg!) {
        fetchRanges.push({
          rangeBeg: beg,
          rangeEnd: dayjs(series.requestedRangeBeg).tz(ET),
        });
      }
      if (end.valueOf() > series.requestedRangeEnd!) {
        fetchRanges.push({
          rangeBeg: dayjs(
            this.windowFromUnixTs(series.requestedRangeEnd)
              .beginUnixTsInclusive,
          ).tz(ET),
          rangeEnd: end,
        });
      }
    }
    return { fetchRanges, beg, end };
  }

  async getCandleReqsFromFetchRanges(
    fetchRanges: FetchRange[],
    end: dayjs.Dayjs,
    now: dayjs.Dayjs,
    symbolLineParams: SymbolLineParams,
  ): Promise<CandleTOHLCVS[]>[] {
    let commonEndpointDeferResolver: () => void | undefined = undefined;
    const commonEndpointDeferPromise = new Promise((resolve) => {
      commonEndpointDeferResolver = resolve;
    });

    if (symbolLineParams.kind === 'key level') {
      const oldLatestKeyLevelFetch = this.latestKeyLevelFetch;
      this.latestKeyLevelFetch = this.latestKeyLevelFetch.then(
        () => commonEndpointDeferPromise,
      );
      await oldLatestKeyLevelFetch;
    } else if (symbolLineParams.kind === 'alert') {
      const oldLatestAlertFetch = this.latestAlertFetch;
      this.latestAlertFetch = this.latestAlertFetch.then(
        () => commonEndpointDeferPromise,
      );
      await oldLatestAlertFetch;
    }

    const candlesReqs = [];
    // chunk requests into day boundaries to enable increased client caching
    for (const { rangeBeg: reqBeg, rangeEnd: reqEnd } of this.rangeSubFetches(
      fetchRanges,
      symbolLineParams.kind,
    )) {
      if (symbolLineParams.kind === 'price') {
        candlesReqs.push(
          DefaultService.getPrices({
            symbol: symbolLineParams.symbol,
            timeGte: reqBeg.tz(ET).format(),
            timeLt: reqEnd.tz(ET).format(),
            limit: 100_000,
            candleDurationMs: this.candleDurationMs,
          }),
        );
      } else if (symbolLineParams.kind === 'hiro') {
        candlesReqs.push(
          DefaultService.getDeltaSignal({
            isFiltered: symbolLineParams.deltaLineParams.isFiltered,
            optionAccumulationType:
              symbolLineParams.deltaLineParams.optionAccumulationType,
            optionOrigin: symbolLineParams.deltaLineParams.optionOrigin,
            symbol: symbolLineParams.symbol,
            timeGte: reqBeg.tz(ET).format(),
            timeLt: reqEnd.tz(ET).format(),
            limit: 100_000,
            candleDurationMs: this.candleDurationMs,
          }),
        );
      } else if (symbolLineParams.kind === 'oldhiro') {
        const reqBegOfDay = reqBeg.tz(ET).startOf('day');
        candlesReqs.push(
          this.fetchAPIWithLog<Record<string, Record<string, RawHiroAndPrice>>>(
            `v11/hiro?all=1&nextExp=1&retail=1&syms=${encodeURIComponent(symbolLineParams.symbol)}&start=${reqBegOfDay.format('YYYY-MM-DD')}&end=${reqBegOfDay.format('YYYY-MM-DD')}`,
          ).then((data: Record<string, Record<string, RawHiroAndPrice>>) => {
            let candles: HiroAndPrice[] = [];
            const symData = data[symbolLineParams.symbol];
            if (symData) {
              const convertedData = convertData(symData, true, ET);
              const originData =
                convertedData[
                  symbolLineParams.deltaLineParams.optionOrigin === 'All'
                    ? 'all'
                    : 'nextExp'
                ];
              if (originData) {
                candles =
                  originData[
                    symbolLineParams.deltaLineParams.optionAccumulationType ===
                    'Total'
                      ? 'TOT'
                      : symbolLineParams.deltaLineParams
                            .optionAccumulationType === 'Calls'
                        ? 'C'
                        : 'P'
                  ] ?? [];
              }
            }
            return {
              candles: candles.map((c: HiroAndPrice) => [
                c.utc_time,
                c.mid_signal,
                c.mid_signal,
                c.mid_signal,
                c.mid_signal,
                0,
              ]),
            };
          }),
        );
      } else if (symbolLineParams.kind === 'key level') {
        const reqBegOfDay = reqBeg.tz(ET).startOf('day');
        candlesReqs.push(
          getEquitiesBySyms(reqBegOfDay, symbolLineParams.symbol).then(
            async (rsp) => {
              if (!rsp) {
                return { candles: [] };
              }
              const v = rsp[
                REVERSE_FIELD_NAME_MAPPING[symbolLineParams.keyLevel]
              ] as number;
              const candles = [];
              const candleVals = [v, v, v, v];
              const alertBegTime =
                this.currentSession === 'extended'
                  ? reqBegOfDay
                  : reqBegOfDay.hour(9).minute(30).second(0);
              let endOfAlert =
                this.currentSession === 'extended'
                  ? reqBegOfDay.add(1, 'day').startOf('day')
                  : reqBegOfDay.hour(16).minute(0).second(0);

              if (endOfAlert > dayjs()) {
                // get the last candle time from the main series:
                endOfAlert = dayjs().subtract(1, 'minute');
                if (this.chartingLibraryWidget) {
                  const tvSeries = this.chartingLibraryWidget
                    .activeChart()
                    .getSeries();
                  const mainSeries = this.getOrCreateSeries(
                    // eslint-disable-next-line
                    tvSeries.symbolSource().symbol,
                  );
                  await mainSeries.latestFetches;
                  const lastMainSeriesCandle =
                    mainSeries.candles[mainSeries.candles.length - 1];
                  if (lastMainSeriesCandle) {
                    endOfAlert = lastMainSeriesCandle.time;
                  }
                }
              }
              candles.push([
                alertBegTime.valueOf() + this.candleDurationMs,
                ...candleVals,
              ]);

              candles.push([
                endOfAlert.valueOf() - this.candleDurationMs,
                ...candleVals,
              ]);

              return { candles };
            },
          ),
        );
      } else if (symbolLineParams.kind === 'alert') {
        const reqBegOfDay = reqBeg.tz(ET).startOf('day');
        const reqEndOfDay = reqBegOfDay.add(1, 'day');
        candlesReqs.push(
          this.fetchAPIWithLog<AlertData[]>(
            `v1/alerts?syms=${encodeURIComponent(symbolLineParams.symbol)}&start=${reqBegOfDay.format('YYYY-MM-DD')}&end=${reqEndOfDay.format('YYYY-MM-DD')}`,
          ).then((alerts: AlertData[]) => {
            const candles =
              alerts
                ?.filter(
                  (a) =>
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
                    a.category ===
                    HiroAlertCategoryMap[symbolLineParams.alertCategory],
                )
                .map((alert): CandleTupleTOHLCV | undefined => {
                  const ts = dayjs(alert.alert_time).valueOf();
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                  const cumul_delta = alert?.payload?.cumul_delta;
                  if (cumul_delta) {
                    return [
                      ts,
                      cumul_delta,
                      cumul_delta,
                      cumul_delta,
                      cumul_delta,
                      0,
                    ];
                  }
                })
                .filter(Boolean) ?? [];

            return { candles };
          }),
        );
      }
    }
    // serialize duplicate requests to certain endpoints, so that most fetches are
    // from browser cache
    if (symbolLineParams.kind === 'key level') {
      commonEndpointDeferResolver(Promise.all(candlesReqs));
    }
    if (symbolLineParams.kind === 'alert') {
      commonEndpointDeferResolver(Promise.all(candlesReqs));
    }

    return candlesReqs;
  }

  rangeSubFetches(
    fetchRanges: FetchRange[],
    symbolKind: SymbolLineKind,
    testNow?: Dayjs,
  ): FetchRange[] {
    const now = testNow ?? dayjs();
    const result: FetchRange[] = [];
    for (const { rangeBeg, rangeEnd } of fetchRanges) {
      let reqBeg = rangeBeg;
      while (reqBeg < rangeEnd) {
        let reqEnd;
        const NON_CHUNKED_SYMBOL_KINDS = new Set([
          'key level',
          'alert',
          'oldhiro',
        ]);
        if (
          now.diff(reqBeg, 'day') < 1 &&
          !NON_CHUNKED_SYMBOL_KINDS.has(symbolKind)
        ) {
          if (now.diff(reqBeg, 'hour') < 1) {
            reqEnd =
              now.diff(reqBeg, 'minute') <= 10
                ? rangeEnd
                : // start of the last 10 minute mark
                  rangeEnd
                    .minute(Math.floor(rangeEnd.minute() / 10) * 10)
                    .second(0)
                    .millisecond(0);
          } else {
            reqEnd = rangeEnd.startOf('hour');
            if (reqEnd <= reqBeg) {
              reqEnd = reqEnd.add(1, 'hour');
            }
          }
        } else {
          reqEnd = reqBeg.add(1, 'day');
        }
        if (reqEnd > rangeEnd) {
          reqEnd = rangeEnd;
        }
        result.push({ rangeBeg: reqBeg, rangeEnd: reqEnd });
        reqBeg = reqEnd;
      }
    }
    return result;
  }

  onVisibleRangeChanged(range: VisibleTimeRange): void {
    if (!range.from) {
      return;
    }
    this.debouncedSaveChart();
    this.beginVisibleRange = dayjs.unix(range.from);
    this.endVisibleRange = dayjs.unix(range.to);
    const { beginUnixTsInclusive: newWindowBegin } = this.windowFromUnixTs(
      range.from,
    );
    if (newWindowBegin != this.windowBegin) {
      if (this.windowBegin && !isFinite(this.rollingWindowMinutes)) {
        this.resetData();
      }
      this.windowBegin = newWindowBegin;
    }

    if (this.broadCastTimeRangeUpdate && range.from && range.to) {
      this.broadCastTimeRangeUpdate({
        from: range.from * 1_000,
        to: range.to * 1_000,
      });
    }
    this.keyLevelsManager.debouncedSetVisibleRange(
      range.from * 1_000,
      range.to * 1_000,
    );
    this.alertsManager.debouncedSetVisibleRange(
      range.from * 1_000,
      range.to * 1_000,
    );
  }

  onIntervalChanged(resolution: ResolutionString): void {
    this.debouncedSaveChart();
    try {
      let newInterval = 0;
      if (resolution.endsWith('S')) {
        newInterval = parseInt(resolution.slice(0, -1)) * 1_000;
      } else {
        newInterval = parseInt(resolution) * 60 * 1_000;
      }
      if (newInterval && this.candleDurationMs != newInterval) {
        this.candleDurationMs = newInterval;
        this.resetData();
      }
    } catch (_) {
      throw new Error(`Unsupported resolution ${resolution}`);
    }
  }

  resetData(): void {
    this.lastDataReset = dayjs();
    this.signalSeries = {};
    this.chartingLibraryWidget?.activeChart().resetData();
    this.chartingLibraryWidget?.resetCache();
    this.keyLevelsManager.removeArtifacts(true);
    void this.keyLevelsManager.debouncedEnsureArtifacts();
    this.alertsManager.removeArtifacts(true);
    void this.alertsManager.debouncedEnsureArtifacts();
  }

  setRollingWindowMinutes(rollingWindowMinutes: number): void {
    if (rollingWindowMinutes != this.rollingWindowMinutes) {
      this.rollingWindowMinutes = rollingWindowMinutes;
      this.resetData();
    }
  }

  getCandleChunkTimeMs(timeMs: number): number {
    return (timeMs / this.candleDurationMs) * this.candleDurationMs;
  }

  tvChartDomManipulation() {
    const tvDoc = (
      document.querySelector('iframe[id^=tradingview]') as HTMLIFrameElement
    )?.contentWindow?.document as DocumentType | undefined;
    if (tvDoc == null) {
      return;
    }

    // access the chart header div in order to install a click handler
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const headerDiv = tvDoc.querySelector('.layout__area--top') as HTMLElement;
    if (headerDiv) {
      headerDiv.addEventListener('click', this.debouncedSaveChart);
    }

    // listen to changes on the TV iframe and then install a click listener on
    // when the setting overlay div is first found
    this.settingsModifierObserver = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.id === 'overlap-manager-root') {
              node.addEventListener('click', this.debouncedSaveChart);
              this.settingsModifierObserver?.disconnect();
            }
          }
        }
      }
    });

    this.settingsModifierObserver.observe(tvDoc, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const oneDayButton = tvDoc.querySelector(
      `button[aria-label="${ONE_DAY_IN_ONE_MIN_INTERVAL_DESC}"]`,
    ) as HTMLElement | undefined;
    if (oneDayButton) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      oneDayButton.addEventListener('click', async (evt: Event) => {
        const chart = this.chartingLibraryWidget?.activeChart();
        if (!chart) {
          return;
        }
        evt.stopPropagation();
        evt.preventDefault();

        await chart.setResolution('1');

        const from = this.windowFromUnixTs(
          getClosestBusinessDay().unix(),
        ).beginUnixTsInclusive;

        // You'd want await chart.setResolution('1') to actually block until the
        // action is completed -- that's normally what returned promises do. But
        // no. They return and then a subsequent call to chart.setVisibleRange is
        // overridden, because the action chart.setResolution and all its effects
        // are not actually done.
        //
        // Thus, you repeatedly tell it your intention to set the visible range
        // to 9:30am-now or 12am-now. The range in time makes it appear quickly
        // at ~500ms and allows for slower machines out to 2.5 seconds. The
        // visible range settings cuts off before it would (hopefully) not
        // override a user's intention to change the time window some other way.
        for (let i = 0; i < 5; ++i) {
          await sleep(500);
          await chart.setVisibleRange({ from, to: dayjs().unix() });
        }
      });
    }
  }

  saveChart() {
    if (!this.enableSavingChart) {
      return;
    }
    console.log('saving chart');
    this.chartingLibraryWidget.save((d) => {
      localStorage.tvHiroChartState = JSON.stringify(d);
    });
  }
}

const candleColorOverrides = (color: string): Record<string, string> => {
  return {
    'candleStyle.borderUpColor': color,
    'candleStyle.borderDownColor': color,
    'candleStyle.borderColor': color,
    'candleStyle.downColor': color,
    'candleStyle.upColor': color,
    'candleStyle.wickColor': color,
    'candleStyle.wickDownColor': color,
    'candleStyle.wickUpColor': color,
  };
};

const SYMBOL_MULT = 1e9;
const DESC_MULT = 1e6;

let lastPrefetch = Promise.resolve();
let anyPrefetchRunning = false;

// higher number means sooner in results
// eslint-disable-next-line react-refresh/only-export-components
export const hiroSearchSortOrderPriority = (
  userInputTokens: string[],
  sym: SearchSymbolResultItem,
): number => {
  let result = 0;

  // symbol field has tokens - highest precedence
  const symLower = sym.symbol.toLowerCase();
  result += userInputTokens.reduce((accum, t, tokenIndex) => {
    const foundStrIdx = symLower.indexOf(t);
    return (
      accum +
      (foundStrIdx === -1
        ? 0
        : SYMBOL_MULT * // very large multiple, for setting precedence higher
          (1_000 * (200 - foundStrIdx) + // higher preference for string matches earlier in symbol name
            (userInputTokens.length - tokenIndex))) // preference for earlier user input tokens
    );
  }, 0);

  // description field has token
  const descLower = sym.description.toLowerCase();
  result += userInputTokens.reduce(
    (accum, t, tokenIndex) =>
      accum +
      DESC_MULT * // medium sized multiple, for setting precedence in the middle
        (!descLower.includes(t) ? 0 : userInputTokens.length - tokenIndex), // preference for earlier user input tokens
    0,
  );

  // price symbols before other symbols, all else being equal
  result += !symLower.includes('  ') ? 1 : 0;

  return result;
};

// eslint-disable-next-line react-refresh/only-export-components
export const hiroSearchSortFnGen =
  (userInputTokens: string[]) =>
  (a: SearchSymbolResultItem, b: SearchSymbolResultItem) =>
    hiroSearchSortOrderPriority(userInputTokens, b) -
    hiroSearchSortOrderPriority(userInputTokens, a);

interface AvailableHiroSignal {
  optionAccumulationType: OptionAccumulationType;
  optionOrigin: OptionOrigin;
  id: string;
  label: string;
}

const availableHiroSignals: AvailableHiroSignal[] =
  OptionAccumulationTypes.flatMap((optionAccumulationType) =>
    HiroIndicatorOptionOrigins.map((optionOrigin) => ({
      optionAccumulationType,
      optionOrigin,
      id: `${optionAccumulationType}_${optionOrigin}`,
      label: `${optionAccumulationType}  ${optionOrigin}`,
    })),
  );

const availableKeyLevels: { id: string; keyLevel: string; labelId: string }[] =
  PriceLineKeys.map((keyLevel) => ({
    keyLevel,
    id: keyLevel.replace(' ', '_'),
    labelId: keyLevel.replace(' ', '_') + '_label',
  }));

export const HiroChartV2 = () => {
  const {
    sym,
    hiroTotalsEnabledPutsCallsDisabled,
    hiroAllTradesEnabled,
    hiroNextExpiryEnabled,
    hiroRollingWindowSeconds,
    hiroEnableCandles,
    update,
  } = useHiroCtx();
  const worker = useRecoilValue(workerState);
  const datafeed = useRef<undefined | HiroChartDatafeed>();
  const setHiroTimeRange = useSetRecoilState(hiroChartTimeRangeState);
  const hiroSyms: Set<string> | undefined = useRecoilValue(hiroSymbolsState);

  const timezone = useRecoilValue(timezoneState);
  const currentGlobalStocksState = useRecoilValue(
    stocksState_SUBSCRIBE_TO_POLLING_RENDER,
  );
  const setCurrentSymHiroOverview = useSetRecoilState(
    currentSymHiroOverviewState,
  );
  const setHiroAlerts = useSetRecoilState(hiroAlertsState);

  const initializingChartContainerProps = useMemo<
    Partial<ChartingLibraryWidgetOptions>
  >(
    () => ({
      interval: '1' as ResolutionString,
      symbol: nameFromSymbolLineParams({
        symbol: sym,
        kind: 'price',
      }),
      timeframe: {
        // from: dayjs('2025-07-14T00:00:00').unix(),
        // to: dayjs('2025-07-15T00:00:00').unix(),
        from: dayjs().tz('America/New_York').startOf('day').unix(),
        to: dayjs().unix(),
      },
      disabled_features: ['create_volume_indicator_by_default'],
      enabled_features: [
        'pre_post_market_sessions',
        'request_only_visible_range_on_reset',
        'seconds_resolution',
      ],
      timezone,
      request_only_visible_range_on_reset: true,
      symbol_search_request_delay: 320,
      overrides: {
        'mainSeriesProperties.sessionId': 'regular',
      },

      // debug: true,
      time_frames: [
        {
          text: '1d',
          resolution: '1',
          description: ONE_DAY_IN_ONE_MIN_INTERVAL_DESC,
        },
        {
          text: '5d',
          resolution: '5',
          description: '5 days in 5 minute intervals',
        },
      ],
      custom_formatters: {
        priceFormatterFactory: (): ISymbolValueFormatter | null => {
          return {
            format: (price: number): string => {
              if (Math.abs(price) >= 1e6) {
                return formatAsCompactNumber(price);
              } else {
                return price.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                });
              }
            },
          };
        },
      },
      custom_indicators_getter: function (
        pineJS: PineJS,
      ): Promise<CustomIndicator[]> {
        return Promise.resolve([
          // HIRO Indicators
          {
            name: 'HIRO Indicators',
            metainfo: {
              _metainfoVersion: 51,

              id: 'HIRO Indicator@tv-basicstudies-1',
              name: 'HIRO Indicators',
              description: HIRO_SIGNALS_INDICATOR_DESCRIPTION,
              shortDescription: 'HIRO Indicators',
              format: { type: 'inherit' },
              linkedToSeries: false, // whether the study price scale should be the same as the main series one.
              is_price_study: true, // whether the study should appear on the main series pane.
              priceScale: StudyTargetPriceScale.Right,
              isCustomIndicator: true,
              plots: availableHiroSignals
                .map(({ id }) => ({
                  id,
                  type: 'line',
                }))
                .concat(
                  availableHiroSignals
                    .map(({ optionAccumulationType, optionOrigin }) => ({
                      optionAccumulationType:
                        optionAccumulationType.toLowerCase(),
                      optionOrigin: optionOrigin.toLowerCase(),
                    }))
                    .flatMap(({ optionAccumulationType, optionOrigin }) =>
                      ohlc_labels.map((ohlc_label) => ({
                        id: `${optionAccumulationType}_${optionOrigin}_${ohlc_label}`,
                        type: `ohlc_${ohlc_label}`,
                        target: `${optionAccumulationType}_${optionOrigin}_candle`,
                      })),
                    ),
                ),

              ohlcPlots: Object.fromEntries(
                availableHiroSignals
                  .map(({ optionAccumulationType, optionOrigin, label }) => ({
                    optionAccumulationType:
                      optionAccumulationType.toLowerCase(),
                    optionOrigin: optionOrigin.toLowerCase(),
                    label,
                  }))
                  .map(({ optionAccumulationType, optionOrigin, label }) => [
                    `${optionAccumulationType}_${optionOrigin}_candle`,
                    {
                      title: `${label} Candles`,
                    },
                  ]),
              ),
              defaults: {
                showLegendValues: false,
                ohlcPlots: Object.fromEntries(
                  availableHiroSignals.map(
                    ({ optionAccumulationType, optionOrigin }) => {
                      const color = theme.palette.hiro.lenses[
                        OPTION_ORIGIN_TO_PALETTE.get(optionOrigin)!
                      ][
                        OPTION_ACCUM_TO_PALETTE.get(optionAccumulationType)!
                      ] as string;
                      return [
                        `${optionAccumulationType.toLowerCase()}_${optionOrigin.toLowerCase()}_candle`,
                        {
                          borderColor: color,
                          color,
                          drawBorder: true,
                          drawWick: true,
                          plottype: 'ohlc_candles',
                          visible: true,
                          wickColor: color,
                        },
                      ];
                    },
                  ),
                ),
                styles: Object.fromEntries(
                  availableHiroSignals.map(
                    ({ optionAccumulationType, optionOrigin, id }) => [
                      id,
                      {
                        linestyle: LineStyle.Solid,
                        linewidth: 1,
                        plottype: 0,
                        trackPrice: false,
                        transparency: 0,
                        visible: true,
                        color: theme.palette.hiro.lenses[
                          OPTION_ORIGIN_TO_PALETTE.get(optionOrigin)!
                        ][
                          OPTION_ACCUM_TO_PALETTE.get(optionAccumulationType)!
                        ] as string,
                        ...candleColorOverrides(
                          theme.palette.hiro.lenses[
                            OPTION_ORIGIN_TO_PALETTE.get(optionOrigin)!
                          ][
                            OPTION_ACCUM_TO_PALETTE.get(optionAccumulationType)!
                          ] as string,
                        ),
                      },
                    ],
                  ),
                ),
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                inputs: Object.fromEntries([
                  ['display_candles', hiroEnableCandles],
                  ...availableHiroSignals.map(
                    ({ optionAccumulationType, optionOrigin, id }) => [
                      id,
                      ((hiroTotalsEnabledPutsCallsDisabled &&
                        optionAccumulationType == 'Total') ||
                        (!hiroTotalsEnabledPutsCallsDisabled &&
                          optionAccumulationType != 'Total')) &&
                        ((hiroAllTradesEnabled && optionOrigin == 'All') ||
                          (hiroNextExpiryEnabled &&
                            optionOrigin == 'NextExpiry')),
                    ],
                  ),
                ]),
              },
              styles: Object.fromEntries(
                availableHiroSignals.map(({ id, label }) => [
                  id,
                  {
                    title: `${label} Line`,
                    histogramBase: 0,
                    joinPoints: false,
                  },
                ]),
              ),
              inputs: [
                {
                  id: 'display_candles',
                  name: 'Display Candles',
                  defval: hiroEnableCandles,
                  type: StudyInputType.Bool,
                  group: 'Display Settings',
                },
                ...availableHiroSignals.map(
                  ({ optionAccumulationType, optionOrigin, label, id }) => ({
                    id,
                    name: label,
                    group: 'Signal Settings',
                    defval:
                      ((hiroTotalsEnabledPutsCallsDisabled &&
                        optionAccumulationType == 'Total') ||
                        (!hiroTotalsEnabledPutsCallsDisabled &&
                          optionAccumulationType != 'Total')) &&
                      ((hiroAllTradesEnabled && optionOrigin == 'All') ||
                        (hiroNextExpiryEnabled &&
                          optionOrigin == 'NextExpiry')),
                    type: StudyInputType.Bool,
                  }),
                ),
              ],
            },
            constructor: function () {
              this.init = function (context, inputCallback) {
                this._context = context;
                this._input = inputCallback;

                const { symbol: mainSymbol = '' } =
                  decodeSymbolLineParams(context.symbol.info.name) ?? {};
                if (!mainSymbol) {
                  return;
                }

                availableHiroSignals.forEach(
                  ({ optionAccumulationType, optionOrigin }) =>
                    context.new_sym(
                      nameFromSymbolLineParams({
                        symbol: mainSymbol,
                        kind: 'hiro',
                        deltaLineParams: {
                          optionAccumulationType,
                          optionOrigin,
                          isFiltered: true,
                        },
                      }),
                      pineJS.Std.period(context),
                    ),
                );
              };
              this.main = function (context, inputCallback) {
                this._context = context;
                this._input = inputCallback;

                // Select the main symbol
                context.select_sym(0);
                const mainSymbolTime = context.new_var(context.symbol.time);

                const enableCandles = Boolean(inputCallback(0));
                update({ hiroEnableCandles: enableCandles });
                const result = availableHiroSignals
                  .map((_, idx) => {
                    if (enableCandles || !inputCallback(idx + 1)) {
                      return undefined;
                    }
                    context.select_sym(idx + 1);
                    const secondarySymbolTime = context.new_var(
                      context.symbol.time,
                    );
                    const secondarySymbolClose = context.new_var(
                      pineJS.Std.close(context),
                    );
                    return secondarySymbolClose.adopt(
                      secondarySymbolTime,
                      mainSymbolTime,
                      1,
                    );
                  })
                  .concat(
                    availableHiroSignals.flatMap((_, idx) => {
                      if (!enableCandles || !inputCallback(idx + 1)) {
                        return ohlc_labels.map((_) => undefined);
                      }
                      context.select_sym(idx + 1);
                      const secondarySymbolTime = context.new_var(
                        context.symbol.time,
                      );
                      return ohlc_labels.map((ohlc_label) => {
                        const secondarySymbolVal = context.new_var(
                          // eslint-disable-next-line
                          pineJS.Std[ohlc_label](context),
                        );
                        return secondarySymbolVal.adopt(
                          secondarySymbolTime,
                          mainSymbolTime,
                          1,
                        );
                      });
                    }),
                  );

                // Select the main symbol again
                context.select_sym(0);
                return result;
              };
            },
          },

          // Key Levels
          {
            name: 'Key Levels',
            metainfo: {
              _metainfoVersion: 53,
              id: 'Key Levels@tv-basicstudies-1',
              description: HIRO_KEY_LEVELS_INDICATOR_DESCRIPTION,
              shortDescription: 'Key Levels',
              format: { type: 'inherit' },
              linkedToSeries: true,
              is_price_study: true,
              is_overlay: false,
              priceScale: StudyTargetPriceScale.Right,
              forceOverlay: false,
              isCustomIndicator: true,
              plots: [{ id: 'keyLevels', type: 'step_line' }],
              defaults: {
                inputs: Object.fromEntries(
                  availableKeyLevels.map(({ id }) => [id, true]),
                ),
              },
              inputs: availableKeyLevels.map(({ keyLevel, id }) => ({
                id,
                name: keyLevel,
                defval: true,
                type: StudyInputType.Bool,
              })),
            },
            // based on https://www.tradingview.com/charting-library-docs/latest/custom_studies/Custom-Studies-Examples/
            constructor: function () {
              this.init = function (context, inputCallback) {
                this._context = context;
                this._input = inputCallback;

                const { symbol: mainSymbol = '' } =
                  decodeSymbolLineParams(context.symbol.info.name) ?? {};

                if (!mainSymbol) {
                  return;
                }

                availableKeyLevels.forEach(({ keyLevel }, idx) =>
                  datafeed.current.keyLevelsManager.setEnable(
                    mainSymbol,
                    inputCallback(idx),
                    keyLevel,
                  ),
                );

                // patch stop function to hook into disabling of the indicator
                // eslint-disable-next-line
                const oldStop = this._context.stop.bind(
                  this._context,
                ) as () => void;

                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                this._context.stop = () => {
                  datafeed.current.keyLevelsManager.setEnable(
                    mainSymbol,
                    false,
                  );
                  oldStop();
                };
              };

              this.main = function (_context, _inputs) {
                return;
              };
            },
          },
          // Alerts
          {
            name: 'Alerts',
            metainfo: {
              _metainfoVersion: 53,
              id: 'Alerts@tv-basicstudies-1',
              description: HIRO_ALERT_INDICATOR_DESCRIPTION,
              shortDescription: 'Alerts',
              format: { type: 'inherit' },
              linkedToSeries: true,
              is_price_study: true,
              is_overlay: false,
              priceScale: StudyTargetPriceScale.Right,
              forceOverlay: false,
              isCustomIndicator: true,
              plots: [{ id: 'hiroAlerts', type: 'step_line' }],
              defaults: {
                inputs: Object.fromEntries(
                  availableAlerts.map(({ id }) => [id, true]),
                ),
              },
            },
            // based on https://www.tradingview.com/charting-library-docs/latest/custom_studies/Custom-Studies-Examples/
            constructor: function () {
              this.init = function (context, inputCallback) {
                this._context = context;
                this._input = inputCallback;

                const { symbol: mainSymbol = '' } =
                  decodeSymbolLineParams(context.symbol.info.name) ?? {};
                if (!mainSymbol) {
                  return;
                }

                availableAlerts.forEach(({ alertCategory }) =>
                  datafeed.current.alertsManager.setEnable(
                    mainSymbol,
                    true,
                    alertCategory,
                  ),
                );

                // patch stop function to hook into disabling of the indicator
                // eslint-disable-next-line
                const oldStop = this._context.stop.bind(
                  this._context,
                ) as () => void;

                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                this._context.stop = () => {
                  datafeed.current.alertsManager.setEnable(mainSymbol, false);
                  oldStop();
                };
              };

              this.main = function (_context, _inputs) {
                return;
              };
            },
          },
        ]);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { fetchAPIWithLog } = useLog('HiroChart');
  const theme = useTheme();

  if (datafeed.current == null) {
    console.log('creating HiroChartDatafeed');
    datafeed.current = new HiroChartDatafeed(
      hiroRollingWindowSeconds / 60,
      setHiroAlerts,
    );

    datafeed.current.fetchAPIWithLog = fetchAPIWithLog;
    // throttle broadcasting too much as dependent code launches fetches immediately
    datafeed.current.broadCastTimeRangeUpdate = debounce(setHiroTimeRange, 700);

    const now = dayjs().tz(ET);

    const periodParams = {
      from: datafeed.current.windowFromUnixTs(now.unix(), MINUTES_IN_DAY)
        .beginUnixTsInclusive,
      to: now.startOf('hour').unix(),
      countback: 1,
    } as PeriodParams;
    // prefetch symbols to hide latency on load, but only run one prefetch at
    // a time
    // eslint-disable-next-line no-constant-condition, no-constant-binary-expression
    if (false && !anyPrefetchRunning) {
      anyPrefetchRunning = true;
      lastPrefetch = Promise.any([
        // watchdog timer in case prefetch fails
        new Promise((resolve) => setTimeout(resolve, 10_000)),
        lastPrefetch.then(() =>
          Promise.all(
            ['price', 'hiro']
              .flatMap((kind) => {
                if (kind === 'price') {
                  return [
                    {
                      name: nameFromSymbolLineParams({
                        symbol: sym,
                        kind,
                      }),
                    } as LibrarySymbolInfo,
                  ];
                } else {
                  return OptionAccumulationTypes.flatMap(
                    (optionAccumulationType) =>
                      HiroIndicatorOptionOrigins.flatMap(
                        (optionOrigin) =>
                          ({
                            name: nameFromSymbolLineParams({
                              symbol: sym,
                              kind: 'hiro',
                              deltaLineParams: {
                                optionAccumulationType,
                                optionOrigin,
                                isFiltered: true,
                              },
                            }),
                          }) as LibrarySymbolInfo,
                      ),
                  );
                }
              })
              .map((symbolInfo: LibrarySymbolInfo) =>
                datafeed.current.getBars(
                  symbolInfo,
                  SUPPORTED_RESOLUTIONS[0],
                  periodParams,
                  () => {}, // onHistoryCallback
                  () => {}, // onErrorCallback -- will be caught by standard call in a sec if there is a failure
                ),
              ),
          ),
        ),
      ]).finally(() => {
        anyPrefetchRunning = false;
      });
    }
  }

  const { sendStreamControlMsg, reconnectSocket } = useStreamingSocketCore({
    enabled: true,
    socket: useRef<WebSocket | null>(null),
    socketRetries: useRef(0),
    onOpen: datafeed.current.reRegisterCandleSubscriptions.bind(
      datafeed.current,
    ),
    onReconnect: datafeed.current.reRegisterCandleSubscriptions.bind(
      datafeed.current,
    ),
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    updateCandleCallback: datafeed.current.listenCandlesStream.bind(
      datafeed.current,
    ),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reconnectSocket, []);

  datafeed.current.sendStreamControlMsg = sendStreamControlMsg;
  // throttle broadcasting too much as dependent code launches fetches immediately
  useEffect(() => {
    datafeed.current.broadCastTimeRangeUpdate = debounce(setHiroTimeRange, 700);
  }, [setHiroTimeRange]);
  datafeed.current.theme = theme;

  datafeed.current.currentGlobalStocksState = currentGlobalStocksState;
  datafeed.current.setCurrentSymHiroOverview = setCurrentSymHiroOverview;

  useEffect(() => {
    const intv = setInterval(
      datafeed.current.resetData.bind(datafeed.current),
      REPLACE_STREAM_DATA_INTERVAL_MS,
    );
    return () => clearInterval(intv);
  }, []);

  const onChartReady = (chartingLibraryWidget: IChartingLibraryWidget) => {
    datafeed.current.chartingLibraryWidget = chartingLibraryWidget;

    void chartingLibraryWidget.changeTheme(
      theme.colorMode == ColorMode.LIGHT ? 'light' : 'dark',
    );

    const chart = chartingLibraryWidget.activeChart();
    // set scale option "Scale price chart only" so key levels don't blow up the scale
    if (!chart.getCheckableActionState('scaleSeriesOnly')) {
      chart.executeActionById('scaleSeriesOnly');
    }

    let createDefaultIndicators = true;
    if (localStorage.tvHiroChartState) {
      try {
        void chartingLibraryWidget
          .load(JSON.parse(localStorage.tvHiroChartState as string) as object)
          .then(() => {
            chart.getTimezoneApi().setTimezone(timezone);
            datafeed.current.onIntervalChanged(chart.resolution());
          });
        createDefaultIndicators = false;
      } catch (e) {
        console.error(
          'Unable to load chart state from localStorage.tvHiroChartState - ',
          e,
        );
      }
    }
    if (createDefaultIndicators) {
      void chart.createStudy(HIRO_SIGNALS_INDICATOR_DESCRIPTION, true, false);
      void chart.dataReady(() => {
        void chart.createStudy(
          HIRO_KEY_LEVELS_INDICATOR_DESCRIPTION,
          true,
          false,
        );
        void chart.createStudy(HIRO_ALERT_INDICATOR_DESCRIPTION, true, false);
      });
      chart.getTimezoneApi().setTimezone(timezone);
    }

    void chart.dataReady(() => {
      setTimeout(
        datafeed.current.tvChartDomManipulation.bind(datafeed.current),
        2_000,
      );
    });

    // used for comparison/debug
    if (localStorage.enableOldHiroComparison) {
      void chart.createStudy(
        'Overlay',
        true,
        false,
        {
          symbol: nameFromSymbolLineParams({
            symbol: sym,
            kind: 'oldhiro',
            deltaLineParams: {
              optionAccumulationType: 'Total',
              optionOrigin: 'All',
              isFiltered: true,
            },
          }),
        },
        {
          style: 1,
          ...candleColorOverrides('purple'),
        },
        { priceScale: 'new-right' },
      );
    }

    // TODO(ross) 2025.08.11 - work in progress
    //setTimeout(() => {
    //  void chart
    //    .createMultipointShape(
    //      [
    //        // { time: dayjs().tz(ET).startOf('day').unix() + 4*60*60, price: 42 },
    //        // { time: dayjs().tz(ET).endOf('day').unix(), price: 42 },
    //        { time: dayjs().tz(ET).startOf('day').unix(), price: 42 },
    //        { time: dayjs().tz(ET).unix(), price: 42 },
    //      ],
    //      {
    //        shape: 'trend_line',
    //        showPrice: false,
    //        lock: true,
    //        //disableSelection: true,
    //        disableSave: true,
    //        disableUndo: true,
    //        text: 'foo',
    //        overrides: {
    //          //showPrice: false,
    //          text: 'foo',
    //          label: 'bar',
    //          linecolor: 'rgba(48, 164, 22, 0.8)',
    //        },
    //      },
    //    )
    //    .then((id) => {
    //      console.log(id);
    //    });
    //}, 2_000);

    // void chart.createShape(
    //   { time: dayjs().startOf('day').unix(), price: 42 },
    //   {
    //     shape: "trend_line",
    //     showPrice: false,
    //     lock: true,
    //     //disableSelection: true,
    //     disableSave: true,
    //     disableUndo: true,
    //     text: "foo",
    //     overrides: {
    //       //showPrice: false,
    //       text: "foo",
    //       label: "bar",
    //       "linecolor": "rgba(48, 164, 22, 0.8)",
    //     }
    //    }
    //  )

    //void chart.createShape(
    //  { price: 42, time: dayjs().tz(ET).startOf('day').unix()},
    //  {
    //    shape: "text",
    //    showPrice: false,
    //    disableSelection: true,
    //    disableSave: true,
    //    disableUndo: true,
    //    text: "foo",
    //    overrides: {
    //      //showPrice: false,
    //      "color": "rgba(48, 164, 22, 0.8)",
    //    }
    //   }
    // )

    chart
      .onVisibleRangeChanged()
      .subscribe(
        null,
        datafeed.current.onVisibleRangeChanged.bind(datafeed.current),
      );

    chart
      .onIntervalChanged()
      .subscribe(
        null,
        datafeed.current.onIntervalChanged.bind(datafeed.current),
      );
  };

  const onHeaderReady = (chartingLibraryWidget: IChartingLibraryWidget) => {
    let totalBtn: HTMLButtonElement | null = null;
    let putCallBtn: HTMLButtonElement | null = null;
    let allTradeBtn: HTMLButtonElement | null = null;
    let nextExpiryTradeBtn: HTMLButtonElement | null = null;

    const btnState = {
      allTradesEnabled: hiroAllTradesEnabled,
      nextExpiryEnabled: hiroNextExpiryEnabled,
      totalEnabled: hiroTotalsEnabledPutsCallsDisabled,
    };

    const visibleIconClass = (visible: boolean): string => {
      return `charting-library-icon-${visible ? '' : 'in'}visible-${chartingLibraryWidget.getTheme()}-theme`;
    };

    const chart = chartingLibraryWidget.activeChart();

    const updateSeriesVisibilities = () => {
      update({
        hiroAllTradesEnabled: btnState.allTradesEnabled,
        hiroNextExpiryEnabled: btnState.nextExpiryEnabled,
        hiroTotalsEnabledPutsCallsDisabled: btnState.totalEnabled,
      });

      const hiro_studies: IStudyApi[] = chart
        .getAllStudies()
        .map((studyId: EntityInfo) => chart.getStudyById(studyId.id))
        .filter(
          (study: IStudyApi) =>
            (study.title as () => string)() ==
            HIRO_SIGNALS_INDICATOR_DESCRIPTION,
        );

      for (const study of hiro_studies) {
        const oldInputValues = study.getInputValues();
        study.setInputValues([
          oldInputValues[0],
          ...zip(study.getInputValues().slice(1), availableHiroSignals).map(
            ([inputValue, { optionAccumulationType, optionOrigin }]: [
              StudyInputValueItem,
              AvailableHiroSignal,
            ]): StudyInputValueItem => ({
              ...inputValue,
              value:
                ((btnState.totalEnabled && optionAccumulationType == 'Total') ||
                  (!btnState.totalEnabled &&
                    optionAccumulationType != 'Total')) &&
                ((btnState.allTradesEnabled && optionOrigin == 'All') ||
                  (btnState.nextExpiryEnabled && optionOrigin == 'NextExpiry')),
            }),
          ),
        ]);
      }
      const visibleIcon = visibleIconClass(true);
      const invisibleIcon = visibleIconClass(false);

      totalBtn!.classList.remove(
        btnState.totalEnabled ? invisibleIcon : visibleIcon,
      );
      totalBtn!.classList.add(
        btnState.totalEnabled ? visibleIcon : invisibleIcon,
      );

      putCallBtn!.classList.remove(
        !btnState.totalEnabled ? invisibleIcon : visibleIcon,
      );
      putCallBtn!.classList.add(
        !btnState.totalEnabled ? visibleIcon : invisibleIcon,
      );

      allTradeBtn!.classList.remove(
        btnState.allTradesEnabled ? invisibleIcon : visibleIcon,
      );
      allTradeBtn!.classList.add(
        btnState.allTradesEnabled ? visibleIcon : invisibleIcon,
      );

      nextExpiryTradeBtn!.classList.remove(
        btnState.nextExpiryEnabled ? invisibleIcon : visibleIcon,
      );
      nextExpiryTradeBtn!.classList.add(
        btnState.nextExpiryEnabled ? visibleIcon : invisibleIcon,
      );
    };

    totalBtn = chartingLibraryWidget.createButton({
      align: 'right',
    });
    totalBtn.setAttribute('title', 'Show combined HIRO signals');
    totalBtn.addEventListener('click', () => {
      btnState.totalEnabled = !btnState.totalEnabled;
      updateSeriesVisibilities();
    });

    totalBtn.textContent = 'Total';
    totalBtn.className += ` charting-library-btn charting-library-icon ${visibleIconClass(btnState.totalEnabled)}`;

    putCallBtn = chartingLibraryWidget.createButton({
      align: 'right',
    });
    putCallBtn.setAttribute('title', 'Show HIRO signals split by puts/calls');
    putCallBtn.addEventListener('click', () => {
      btnState.totalEnabled = !btnState.totalEnabled;
      updateSeriesVisibilities();
    });
    putCallBtn.textContent = 'Put/Call';
    putCallBtn.className += ` charting-library-btn charting-library-icon ${visibleIconClass(!btnState.totalEnabled)}`;

    allTradeBtn = chartingLibraryWidget.createButton({
      align: 'right',
    });
    allTradeBtn.setAttribute('title', 'Show all options trades');
    allTradeBtn.addEventListener('click', () => {
      btnState.allTradesEnabled = !btnState.allTradesEnabled;
      updateSeriesVisibilities();
    });

    allTradeBtn.textContent = 'All Trades';
    allTradeBtn.className += ` charting-library-btn charting-library-icon ${visibleIconClass(btnState.allTradesEnabled)}`;

    nextExpiryTradeBtn = chartingLibraryWidget.createButton({
      align: 'right',
    });
    nextExpiryTradeBtn.setAttribute(
      'title',
      'Show options trades for the next available expiration for this symbol',
    );
    nextExpiryTradeBtn.addEventListener('click', () => {
      btnState.nextExpiryEnabled = !btnState.nextExpiryEnabled;
      updateSeriesVisibilities();
    });

    nextExpiryTradeBtn.textContent = 'Next Expiry';
    nextExpiryTradeBtn.className += ` charting-library-btn charting-library-icon ${visibleIconClass(btnState.nextExpiryEnabled)}`;

    const minutesToRollingDropdown = (minutes: number): string => {
      switch (minutes) {
        case 1:
          return '1m';
        case 5:
          return '5m';
        case 10:
          return '10m';
        case 30:
          return '30m';
        case 60:
          return '1h';
        case 4 * 60:
          return '4h';
        case 24 * 60:
          return '1d';
        default:
          return isFinite(minutes) ? '1d' : '∞';
      }
    };

    let dropdownRef: IDropdownApi | null = null;
    const onSelect = (rollingWindowMinutes: number) => () => {
      if (dropdownRef) {
        dropdownRef.applyOptions({
          title: `Rolling Window: ${minutesToRollingDropdown(rollingWindowMinutes)}`,
        });
      }
      update({ hiroRollingWindowSeconds: rollingWindowMinutes * 60 });
      datafeed.current.setRollingWindowMinutes(rollingWindowMinutes);
    };

    void chartingLibraryWidget
      .createDropdown({
        title: `Rolling Window: ${minutesToRollingDropdown(hiroRollingWindowSeconds / 60)}`,
        items: [
          { title: '1 minute', onSelect: onSelect(1) },
          { title: '5 minutes', onSelect: onSelect(5) },
          { title: '10 minutes', onSelect: onSelect(10) },
          { title: '30 minutes', onSelect: onSelect(30) },
          { title: '1 hour', onSelect: onSelect(60) },
          { title: '4 hours', onSelect: onSelect(240) },
          { title: '•• 1 day ••', onSelect: onSelect(1_440) },
          { title: '∞', onSelect: onSelect(Infinity) },
        ],
        align: 'right',
      })
      .then((dropdown) => {
        dropdownRef = dropdown;
      });

    // reset scale to `auto` when the symbol changes
    chart.onSymbolChanged().subscribe(null, () => {
      const sym = decodeSymbolLineParams(chart.symbol());
      if (sym?.symbol.length) {
        // types not propagated out of recoil `selector` properly
        // prettier-ignore
        // eslint-disable-next-line
        const casedSym: string = hiroSyms?.values().find((s: string) => s.toLowerCase() === sym.symbol.toLowerCase()) ?? sym.symbol;
        update({ sym: casedSym });
      }
      // eslint-disable-next-line
      chart.getSeries().priceScale().setAutoScale(true);
      chart
        .getPanes()
        .forEach((pane) =>
          pane.getRightPriceScales().forEach((s) => s.setAutoScale(true)),
        );
      const chartSession =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        (chart.getSeries().properties()?.sessionId?.value?.() ??
          'regular') as string;
      if (datafeed.current.currentSession != chartSession) {
        datafeed.current.currentSession = chartSession;
        datafeed.current.resetData();
      }
    });
  };
  useEffect(
    () => {
      if (!worker) {
        return;
      }
      return unsupressedSetInterval(
        worker,
        () => void datafeed.current.alertsManager.ensureArtifacts(),
        REFETCH_ALERTS_INTERVAL_MS,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [worker, datafeed.current],
  );

  useEffect(
    () => {
      const symbol = nameFromSymbolLineParams({
        symbol: sym,
        kind: 'price',
      });
      initializingChartContainerProps.symbol = symbol;
      try {
        const chart = datafeed.current.chartingLibraryWidget?.activeChart();
        if (chart != null) {
          datafeed.current.resetData();
          void chart.setSymbol(symbol);
        }
      } catch (_e) {
        // ignore race condition between re-initialization of chart and resetting
        // of chart data
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sym],
  );

  return (
    <ChartingLibrary
      initializingChartContainerProps={initializingChartContainerProps}
      datafeed={datafeed.current}
      onChartReady={onChartReady}
      onHeaderReady={onHeaderReady}
      destructor={datafeed.current.destructor.bind(datafeed.current)}
    />
  );
};
